import React, { useState } from 'react';
// import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useAlertContext } from '../context/AlertContext';
// import { API_URL } from '../config/api';
import { submitProposal } from '../services/projectService';
import { FaTimes, FaLaptopCode, FaGithub } from 'react-icons/fa';

const ProposalModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    githubLink: '',
    difficulty: 'Beginner',
    techStack: '', // Comma separated
    tags: '' // Comma separated
  });
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const { showAlert } = useAlertContext();

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
        const token = localStorage.getItem('token');
        if (!token) {
             showAlert({ type: 'error', message: 'You must be logged in to submit a proposal.' });
             setLoading(false);
             return;
        }

        // Process tech stack and tags
        const processedTechStack = formData.techStack.split(',').map(item => ({
            name: item.trim(),
            icon: 'FaCode', // Default, backend could enhance or we can ask user but let's keep it simple
            color: 'text-gray-500'
        })).filter(item => item.name);

        const processedTags = formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag);

        const payload = {
            ...formData,
            techStack: processedTechStack,
            tags: processedTags
        };

        const config = {
            headers: { Authorization: `Bearer ${token}` }
        };



        const res = await submitProposal(payload);

        if (res.success) { // submitProposal returns response.data, so we check res.success directly (assuming it returns { success: true, ... })
            showAlert({ type: 'success', message: 'Proposal submitted successfully! Pending admin approval.' });
            onClose();
            setFormData({
                title: '',
                description: '',
                githubLink: '',
                difficulty: 'Beginner',
                techStack: '',
                tags: ''
            });
        }
    } catch (err) {
        console.error(err);
        showAlert({ type: 'error', message: err.response?.data?.error || 'Failed to submit proposal.' });
    } finally {
        setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl animate-in zoom-in-95 duration-200 relative">
        
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-gray-50/50 sticky top-0 z-10 backdrop-blur-md">
            <h2 className="heading-tertiary text-gray-900 flex items-center gap-2">
                <FaLaptopCode className="text-primary-600" />
                Submit Project Proposal
            </h2>
            <button 
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all"
            >
                <FaTimes className="w-5 h-5" />
            </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-8 space-y-6">
            
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-blue-800 text-sm mb-6">
                <strong>Note:</strong> Your proposal will be reviewed by an admin before appearing on the public list. Please ensure the repository is public and includes a README.
            </div>

            <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Project Title</label>
                <input 
                    type="text" 
                    required
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-4 focus:ring-primary-100 focus:border-primary-500 transition-all outline-none"
                    placeholder="e.g. My Awesome Tool"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                />
            </div>

            <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Description</label>
                <textarea 
                    required
                    rows="4"
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-4 focus:ring-primary-100 focus:border-primary-500 transition-all outline-none resize-none"
                    placeholder="Briefly describe what your project does..."
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                ></textarea>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                 <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">GitHub Repository URL</label>
                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FaGithub className="text-gray-400" />
                        </div>
                        <input 
                            type="url" 
                            required
                            className="w-full pl-10 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-4 focus:ring-primary-100 focus:border-primary-500 transition-all outline-none"
                            placeholder="https://github.com/username/repo"
                            value={formData.githubLink}
                            onChange={(e) => setFormData({...formData, githubLink: e.target.value})}
                        />
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">Difficulty Level</label>
                    <select 
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-4 focus:ring-primary-100 focus:border-primary-500 transition-all outline-none appearance-none"
                        value={formData.difficulty}
                        onChange={(e) => setFormData({...formData, difficulty: e.target.value})}
                    >
                        <option value="Beginner">Beginner</option>
                        <option value="Intermediate">Intermediate</option>
                        <option value="Advanced">Advanced</option>
                    </select>
                </div>
            </div>

            <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Tech Stack (Comma separated)</label>
                <input 
                    type="text" 
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-4 focus:ring-primary-100 focus:border-primary-500 transition-all outline-none"
                    placeholder="e.g. React, Node.js, Python"
                    value={formData.techStack}
                    onChange={(e) => setFormData({...formData, techStack: e.target.value})}
                />
            </div>
            
             <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Tags (Comma separated)</label>
                <input 
                    type="text" 
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-4 focus:ring-primary-100 focus:border-primary-500 transition-all outline-none"
                    placeholder="e.g. AI, Web, Tooling"
                    value={formData.tags}
                    onChange={(e) => setFormData({...formData, tags: e.target.value})}
                />
            </div>

            <div className="pt-4 flex justify-end gap-3">
                <button 
                    type="button"
                    onClick={onClose}
                    className="px-6 py-2.5 rounded-xl font-bold text-gray-600 hover:bg-gray-100 transition-all"
                >
                    Cancel
                </button>
                <button 
                    type="submit"
                    disabled={loading}
                    className="px-8 py-2.5 rounded-xl font-bold text-white bg-primary-600 hover:bg-primary-700 transition-all shadow-lg hover:shadow-primary-500/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                    {loading ? <div className="loading-spinner w-4 h-4 border-2 mr-2"></div> : null}
                    Submit Proposal
                </button>
            </div>
        </form>

      </div>
    </div>
  );
};

export default ProposalModal;
